import { useEffect, useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { auth$ } from '../../store';
import RichMarkdown from '../RichMarkdown';
import { ICON_SIZE } from '../../design-system';

const READ_REPORTS_KEY = 'reedle-dashboard-read-reports-v1';

const loadReadReportIds = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(READ_REPORTS_KEY) || '[]');
    return new Set(Array.isArray(stored) ? stored : []);
  } catch (error) {
    console.warn('[DashboardNotifications] Invalid read-state cache', error);
    return new Set();
  }
};

const persistReadReportIds = (ids) => {
  try {
    localStorage.setItem(READ_REPORTS_KEY, JSON.stringify([...ids]));
  } catch (error) {
    console.warn('[DashboardNotifications] Could not persist read state', error);
  }
};

const formatWeekRange = (weekStart, weekEnd, locale) => {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const exclusiveEnd = new Date(`${weekEnd}T00:00:00Z`);
  const end = new Date(exclusiveEnd);
  end.setUTCDate(end.getUTCDate() - 1);

  const formatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

const DashboardNotifications = ({ onUnreadCountChange }) => {
  const { t, i18n } = useTranslation(['articles', 'common']);
  const userId = useSelector(() => auth$.user.get()?.id);
  const [reportState, setReportState] = useState({
    userId: null,
    reports: [],
    error: null,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [readIds, setReadIds] = useState(loadReadReportIds);

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;

    const fetchReports = async () => {
      const { data, error: queryError } = await supabase
        .from('reedle_weekly_reports')
        .select('id, week_start, week_end, stats, top_articles, summary, status, error_message, created_at, updated_at')
        .eq('user_id', userId)
        .in('status', ['ready', 'error'])
        .order('updated_at', { ascending: false })
        .limit(8);

      if (cancelled) return;

      if (queryError) {
        console.error('[DashboardNotifications] Fetch failed', queryError);
        setReportState({ userId, reports: [], error: queryError });
      } else {
        setReportState({ userId, reports: data || [], error: null });
      }
    };

    fetchReports();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const hasCurrentResult = reportState.userId === userId;
  const reports = hasCurrentResult ? reportState.reports : [];
  const error = hasCurrentResult ? reportState.error : null;
  const loading = Boolean(userId) && !hasCurrentResult;

  const unreadCount = reports.reduce(
    (count, report) => count + (readIds.has(report.id) ? 0 : 1),
    0,
  );

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [onUnreadCountChange, unreadCount]);

  const toggleReport = (reportId) => {
    const willExpand = expandedId !== reportId;
    setExpandedId(willExpand ? reportId : null);

    if (!willExpand || readIds.has(reportId)) return;

    setReadIds((current) => {
      const next = new Set(current);
      next.add(reportId);
      persistReadReportIds(next);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="dashboard-notifications dashboard-notifications--state" aria-busy="true">
        <div className="dashboard-notification-skeleton" />
        <div className="dashboard-notification-skeleton" />
        <div className="dashboard-notification-skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-notifications dashboard-notifications--state" role="status">
        {t('articles:dashboard.reportFailed')}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="dashboard-notifications dashboard-notifications--state">
        {t('articles:dashboard.weeklyReportEmpty')}
      </div>
    );
  }

  return (
    <div className="dashboard-notifications">
      {reports.map((report) => {
        const expanded = expandedId === report.id;
        const unread = !readIds.has(report.id);
        const stats = report.stats || {};
        const topArticles = report.top_articles || [];
        const panelId = `dashboard-notification-${report.id}`;

        return (
          <article
            key={report.id}
            className={`dashboard-notification-item${unread ? ' is-unread' : ''}`}
          >
            <button
              type="button"
              className="dashboard-notification-trigger"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => toggleReport(report.id)}
            >
              <span className="dashboard-notification-icon" aria-hidden="true">
                <Bell size={ICON_SIZE.sm} />
                {unread && <span className="dashboard-notification-unread-dot" />}
              </span>
              <span className="dashboard-notification-heading">
                <strong>{t('articles:dashboard.weeklyReport')}</strong>
                <span>
                  {formatWeekRange(report.week_start, report.week_end, i18n.resolvedLanguage || i18n.language)}
                </span>
              </span>
              <ChevronDown
                size={ICON_SIZE.sm}
                className="dashboard-notification-chevron"
                aria-hidden="true"
              />
            </button>

            {expanded && (
              <div
                id={panelId}
                className="dashboard-notification-body"
                role="region"
              >
                {report.status === 'error' ? (
                  <p className="dashboard-notification-error">
                    {report.error_message || t('articles:dashboard.reportFailed')}
                  </p>
                ) : (
                  <>
                    <div className="dashboard-notification-stats">
                      <span>
                        <strong>{stats.blocks_read ?? 0}</strong>
                        {t('articles:dashboard.blocks')}
                      </span>
                      <span>
                        <strong>{stats.articles_read ?? 0}</strong>
                        {t('articles:dashboard.articles')}
                      </span>
                      <span>
                        <strong>{stats.highlights_created ?? 0}</strong>
                        {t('articles:dashboard.highlights')}
                      </span>
                    </div>

                    {report.summary && (
                      <div className="dashboard-notification-summary">
                        <RichMarkdown>{report.summary}</RichMarkdown>
                      </div>
                    )}

                    {topArticles.length > 0 && (
                      <div className="dashboard-notification-articles">
                        {topArticles.slice(0, 3).map((article) => (
                          <Link
                            key={article.article_id}
                            className="dashboard-notification-article"
                            to={`/article/${article.article_id}`}
                          >
                            <FileText size={ICON_SIZE.xs} aria-hidden="true" />
                            <span>{article.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

export default DashboardNotifications;
